import { useMemo, useState } from 'react';
import type { ApiUser } from '@catalyst-psa/shared';
import { API_BASE_PATH, API_KEY_HEADER, DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { env } from '../../config/env.js';
import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/date.js';
import { useRoles } from '../roles/api.js';
import { ApiUserFormModal } from './ApiUserFormModal.js';
import { useApiUsers, useDeleteApiUser, useRevokeApiUser } from './api.js';

interface StatusDisplay {
  label: string;
  tone: 'green' | 'gray' | 'amber' | 'red';
}

function statusOf(apiUser: ApiUser): StatusDisplay {
  if (apiUser.revokedAt) {
    return { label: 'Revoked', tone: 'red' };
  }
  if (apiUser.expiresAt && new Date(apiUser.expiresAt).getTime() < Date.now()) {
    return { label: 'Expired', tone: 'amber' };
  }
  return apiUser.isActive
    ? { label: 'Active', tone: 'green' }
    : { label: 'Inactive', tone: 'gray' };
}

/** Display header name (Node lower-cases headers; show the conventional casing). */
const API_KEY_HEADER_DISPLAY = 'X-Api-Key';

function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

/** Global connection details + links to the interactive docs and OpenAPI download. */
function ConnectionCard(): JSX.Element {
  const origin = (env.apiBaseUrl || window.location.origin).replace(/\/$/, '');
  const baseUrl = `${origin}${API_BASE_PATH}`;
  const docsUrl = `${origin}/docs`;
  const openApiUrl = `${baseUrl}/docs/openapi.json`;
  const example = `curl -H "${API_KEY_HEADER_DISPLAY}: cpsa_xxxxxxxx_…" \\\n  ${baseUrl}/customers`;

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Connecting to the API</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Everything an integration needs to authenticate and call Catalyst PSA.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.open(docsUrl, '_blank', 'noopener,noreferrer')}
          >
            <Icon name="reports" className="h-4 w-4" />
            API docs
          </Button>
          <Button
            size="sm"
            onClick={() => window.open(openApiUrl, '_blank', 'noopener,noreferrer')}
          >
            Download OpenAPI
          </Button>
        </div>
      </div>

      <dl className="grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Base URL</dt>
          <dd className="mt-1 flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-800">
              {baseUrl}
            </code>
            <CopyButton value={baseUrl} />
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Authentication header
          </dt>
          <dd className="mt-1 flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-800">
              {API_KEY_HEADER_DISPLAY}: &lt;your key&gt;
            </code>
            <CopyButton value={API_KEY_HEADER_DISPLAY} />
          </dd>
        </div>
      </dl>

      <div className="mt-4">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
          Example request
        </p>
        <pre className="overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800">
          {example}
        </pre>
        <p className="mt-2 text-xs text-slate-500">
          Send your key in the <code className="font-mono">{API_KEY_HEADER_DISPLAY}</code> header
          (its raw name is <code className="font-mono">{API_KEY_HEADER}</code>). Keys are shown once
          at creation. A request&apos;s access is governed by the roles assigned to the API user.
          The interactive docs let you try endpoints with the <strong>Authorize</strong> button.
        </p>
      </div>
    </div>
  );
}

export function ApiUsersPage(): JSX.Element {
  const { isAdmin } = useAuth();

  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ApiUser | null>(null);
  const [revoking, setRevoking] = useState<ApiUser | null>(null);
  const [deleting, setDeleting] = useState<ApiUser | null>(null);

  const openCreate = (): void => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (apiUser: ApiUser): void => {
    setEditing(apiUser);
    setModalOpen(true);
  };

  const query = useApiUsers({ page, pageSize: DEFAULT_PAGE_SIZE });
  const rolesQuery = useRoles({ page: 1, pageSize: 200 });
  const revokeMutation = useRevokeApiUser();
  const deleteMutation = useDeleteApiUser();

  const roleNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const role of rolesQuery.data?.data ?? []) {
      map.set(role.id, role.name);
    }
    return map;
  }, [rolesQuery.data]);

  const apiUsers = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const handleRevoke = (): void => {
    if (!revoking) {
      return;
    }
    revokeMutation.mutate(revoking.id, { onSuccess: () => setRevoking(null) });
  };

  const handleDelete = (): void => {
    if (!deleting) {
      return;
    }
    deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
  };

  return (
    <div>
      <PageHeader
        title="API Users"
        description="Issue API keys for programmatic access. Keys are shown once at creation."
        actions={
          isAdmin ? (
            <Button onClick={openCreate}>
              <Icon name="apiKeys" className="h-4 w-4" />
              New API user
            </Button>
          ) : undefined
        }
      />

      <ConnectionCard />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Key prefix</th>
              <th className="px-4 py-3 font-medium">Roles</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last used</th>
              <th className="px-4 py-3 font-medium">Expires</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={7}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={apiUsers.length === 0}
              emptyTitle="No API users found"
              emptyDescription="Create your first API user to enable programmatic access."
              emptyAction={isAdmin ? <Button onClick={openCreate}>New API user</Button> : undefined}
            >
              {apiUsers.map((apiUser) => {
                const status = statusOf(apiUser);
                const canRevoke = isAdmin && !apiUser.revokedAt;
                return (
                  <tr
                    key={apiUser.id}
                    className={isAdmin ? 'cursor-pointer hover:bg-slate-50' : 'hover:bg-slate-50'}
                    onClick={isAdmin ? () => openEdit(apiUser) : undefined}
                  >
                    <td className="px-4 py-3 font-medium text-slate-900">{apiUser.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">
                      {apiUser.keyPrefix}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {apiUser.roleIds.length === 0
                        ? '—'
                        : apiUser.roleIds.map((id) => roleNames.get(id) ?? '…').join(', ')}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(apiUser.lastUsedAt)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(apiUser.expiresAt)}</td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin ? (
                        <div
                          className="flex justify-end gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {canRevoke ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-amber-600 hover:bg-amber-50"
                              onClick={() => setRevoking(apiUser)}
                            >
                              Revoke
                            </Button>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 hover:bg-rose-50"
                            onClick={() => setDeleting(apiUser)}
                          >
                            Delete
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </DataState>
          </tbody>
        </table>

        {pageInfo && apiUsers.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <ApiUserFormModal isOpen={modalOpen} apiUser={editing} onClose={() => setModalOpen(false)} />

      <ConfirmDialog
        isOpen={revoking !== null}
        title="Revoke API key"
        message={`Revoke "${revoking?.name ?? ''}"? The key will stop working immediately.`}
        confirmLabel="Revoke"
        destructive
        isConfirming={revokeMutation.isPending}
        onConfirm={handleRevoke}
        onCancel={() => setRevoking(null)}
      />

      <ConfirmDialog
        isOpen={deleting !== null}
        title="Delete API user"
        message={`Delete "${deleting?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
